import { useFlags } from 'launchdarkly-react-client-sdk';
import { createContext, useContext, useEffect, useRef, useState } from 'react';

import { switchContextAndVerify } from '../../api/memberships/switchContext';
import useOrganizationImpersonation from '../../hooks/useOrganizationImpersonation';
import { useAuthUser } from '../../hooks/useUser';
import { logError } from '../../rollbar';

const MARKETPLACE_APP_URL = import.meta.env.VITE_WS_MARKETPLACE_APP_URL;
// Token-authenticated route (MP-6406): the nav is fetched with the Firebase
// Bearer token instead of a Nova session cookie, which cross-site cookie
// policy (SameSite) withholds when moxit and the marketplace live on
// different registrable domains.
const NAV_PATH = '/firebase-api/mirror-nav';
const CACHE_KEY = 'mirrored-left-nav-cache';
const PROGRAM_CONTEXT_TIMEOUT_MS = 5_000;

export function mirrorNavRequestUrl(baseUrl: string, selectedSchoolId: string | null): string {
  const query = selectedSchoolId ? `?selected_school_id=${encodeURIComponent(selectedSchoolId)}` : '';
  return `${baseUrl}${NAV_PATH}${query}`;
}

interface CacheEntry {
  html: string;
  cssHrefs: string[];
  selectedSchoolId: string | null;
  userId: string | null;
  organizationId: string | null;
}

export function readMirrorNavCache(
  selectedSchoolId: string | null,
  userId: string | null,
  organizationId: string | null
): CacheEntry | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed.html !== 'string') return null;
    if (parsed.selectedSchoolId !== selectedSchoolId) return null;
    if (parsed.userId !== userId) return null;
    if ((parsed.organizationId ?? null) !== organizationId) return null;
    return {
      html: parsed.html,
      cssHrefs: Array.isArray(parsed.cssHrefs) ? parsed.cssHrefs : [],
      selectedSchoolId,
      userId,
      organizationId,
    };
  } catch {
    return null;
  }
}

function writeCache(entry: CacheEntry): void {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(entry));
  } catch {
    // Storage full or unavailable — non-critical, ignore.
  }
}

function clearCache(): void {
  try {
    sessionStorage.removeItem(CACHE_KEY);
  } catch {
    return;
  }
}

/**
 * Ensure all CSS hrefs are loaded in the document. Injects any missing
 * `<link rel="stylesheet">` elements and returns a promise that resolves
 * once every link has loaded (or errored). Already-present links resolve
 * immediately. Used by `MirroredLeftNav` to gate visibility on CSS
 * readiness, preventing dimension jitter on cache-hit renders.
 */
export function ensureCssLoaded(hrefs: string[]): Promise<void> {
  const promises: Promise<void>[] = [];

  for (const href of hrefs) {
    const alreadyLoaded = Array.from(document.querySelectorAll('link[rel="stylesheet"]')).some(
      (existing) => existing.getAttribute('href') === href
    );
    if (alreadyLoaded) continue;

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    promises.push(
      new Promise<void>((resolve) => {
        link.onload = () => resolve();
        link.onerror = () => resolve();
      })
    );
    document.head.appendChild(link);
  }

  return Promise.all(promises).then(() => {});
}

interface MirroredLeftNavState {
  /** The raw #left-nav outerHTML from Nova, or null if not yet loaded / error. */
  navHtml: string | null;
  /** Whether the feature is enabled (flag on + fetch succeeded). */
  isActive: boolean;
  /** Whether the flag is on and the fetch is still in flight. Consumers should
   *  avoid rendering the legacy nav while this is true to prevent a flash. */
  isLoading: boolean;
  /** Resolved CSS hrefs from Nova. Used by MirroredLeftNav to ensure stylesheets
   *  are loaded before revealing the nav (prevents dimension jitter). */
  cssHrefs: string[];
}

type ScopedMirroredLeftNavState = MirroredLeftNavState & {
  selectedSchoolId: string | null;
  userId: string | null;
  organizationId: string | null;
};

const MirroredLeftNavContext = createContext<MirroredLeftNavState>({
  navHtml: null,
  isActive: false,
  isLoading: false,
  cssHrefs: [],
});

/**
 * Provider that fetches the mirrored left nav HTML from Nova once and shares
 * the result with all consumers via context. Place this above both the desktop
 * and mobile layout containers so the fetch happens exactly once.
 *
 * Handles: feature flag check, fetch, HTML parsing, error detection, and CSS
 * loading. Falls back to `isActive: false` on any error so consumers can
 * render the legacy navigation instead.
 */
export function MirroredLeftNavProvider({ children }: { children: React.ReactNode }) {
  const { isMirroredLeftNavEnabled } = useFlags();
  const { currentUser, token } = useAuthUser();
  const userId = currentUser?.uid ?? null;
  const { isImpersonatingOrganization, impersonatedOrganizationId, defaultOrganizationId, activeOrganization } =
    useOrganizationImpersonation();
  const expectedOrganizationId = impersonatedOrganizationId || defaultOrganizationId;
  const confirmedNoOrganization =
    !isImpersonatingOrganization &&
    !expectedOrganizationId &&
    !activeOrganization?.id &&
    activeOrganization?.fetching === false;
  const programContextPending =
    !confirmedNoOrganization &&
    (!expectedOrganizationId ||
      (isImpersonatingOrganization && activeOrganization?.fetching === true) ||
      activeOrganization?.id !== expectedOrganizationId);
  const programContextKey = programContextPending
    ? `${expectedOrganizationId ?? ''}:${activeOrganization?.id ?? ''}`
    : null;
  const [timedOutProgramContextKey, setTimedOutProgramContextKey] = useState<string | null>(null);
  const programContextTimedOut = programContextKey !== null && timedOutProgramContextKey === programContextKey;
  const isProgramContextPending = programContextPending && !programContextTimedOut;
  const isProgramNavUnavailable =
    programContextTimedOut ||
    (isImpersonatingOrganization && !programContextPending && !activeOrganization?.marketplaceSchoolId);
  // A director's selectedSchoolId is always null, so the org id is what tells schools apart.
  const organizationId = activeOrganization?.id ?? null;
  const selectedSchoolId =
    isImpersonatingOrganization && !programContextPending && activeOrganization?.marketplaceSchoolId
      ? String(activeOrganization.marketplaceSchoolId)
      : null;
  const cached =
    isMirroredLeftNavEnabled && !isProgramContextPending && !isProgramNavUnavailable
      ? readMirrorNavCache(selectedSchoolId, userId, organizationId)
      : null;
  const [state, setState] = useState<ScopedMirroredLeftNavState>({
    navHtml: cached?.html ?? null,
    isActive: cached !== null,
    isLoading: isMirroredLeftNavEnabled && !isProgramNavUnavailable && (isProgramContextPending || cached === null),
    cssHrefs: cached?.cssHrefs ?? [],
    selectedSchoolId,
    userId,
    organizationId,
  });
  const activeRequestIdRef = useRef(0);
  // Lets the async nav fetch skip state updates once the provider is gone.
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    setTimedOutProgramContextKey(null);

    if (!programContextKey) {
      return;
    }

    const timeout = setTimeout(() => setTimedOutProgramContextKey(programContextKey), PROGRAM_CONTEXT_TIMEOUT_MS);
    return () => clearTimeout(timeout);
  }, [programContextKey]);

  // Clear cache when the flag is turned off.
  useEffect(() => {
    if (!isMirroredLeftNavEnabled) clearCache();
  }, [isMirroredLeftNavEnabled]);

  useEffect(() => {
    const scopedCache =
      isMirroredLeftNavEnabled && !isProgramContextPending && !isProgramNavUnavailable
        ? readMirrorNavCache(selectedSchoolId, userId, organizationId)
        : null;
    activeRequestIdRef.current += 1;
    setState({
      navHtml: scopedCache?.html ?? null,
      isActive: scopedCache !== null,
      isLoading:
        isMirroredLeftNavEnabled && !isProgramNavUnavailable && (isProgramContextPending || scopedCache === null),
      cssHrefs: scopedCache?.cssHrefs ?? [],
      selectedSchoolId,
      userId,
      organizationId,
    });
  }, [
    selectedSchoolId,
    userId,
    organizationId,
    isProgramContextPending,
    isProgramNavUnavailable,
    isMirroredLeftNavEnabled,
  ]);

  useEffect(() => {
    if (!isMirroredLeftNavEnabled || isProgramContextPending || isProgramNavUnavailable) return;

    // Wait for the Firebase token — it authenticates the nav fetch.
    if (!token) return;

    const requestId = activeRequestIdRef.current + 1;
    activeRequestIdRef.current = requestId;
    const scopedCache = readMirrorNavCache(selectedSchoolId, userId, organizationId);

    if (!MARKETPLACE_APP_URL) {
      console.warn('MirroredLeftNav: VITE_WS_MARKETPLACE_APP_URL is not configured.');
      if (!scopedCache) {
        setState({
          navHtml: null,
          isActive: false,
          isLoading: false,
          cssHrefs: [],
          selectedSchoolId,
          userId,
          organizationId,
        });
      }
      return;
    }

    const hasCachedHtml = scopedCache?.html != null;
    const abortController = new AbortController();

    const fetchNav = async () => {
      try {
        const url = mirrorNavRequestUrl(MARKETPLACE_APP_URL, selectedSchoolId);
        const response = await fetch(url, {
          headers: { Accept: 'text/html', Authorization: `Bearer ${token}` },
          signal: abortController.signal,
        });

        if (!isMountedRef.current || activeRequestIdRef.current !== requestId) return;

        const fail = () => {
          if (!hasCachedHtml && isMountedRef.current) {
            setState({
              navHtml: null,
              isActive: false,
              isLoading: false,
              cssHrefs: [],
              selectedSchoolId,
              userId,
              organizationId,
            });
          }
        };

        if (!response.ok) {
          console.warn(`MirroredLeftNav: fetch returned ${response.status}.`);
          fail();
          return;
        }

        const html = await response.text();
        if (!isMountedRef.current || activeRequestIdRef.current !== requestId) return;

        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const leftNav = doc.getElementById('left-nav');

        if (!leftNav) {
          console.warn('MirroredLeftNav: #left-nav element not found in response.');
          fail();
          return;
        }

        // The Nova template renders error divs (with IDs containing "error")
        // when the user is not authenticated, not a director, or the feature
        // flag is disabled on the Nova side.
        if (leftNav.querySelector('[id*="error"]')) {
          console.warn('MirroredLeftNav: error element detected in response.');
          fail();
          return;
        }

        // Resolve CSS hrefs (for caching) and load any not yet in the document.
        const cssLinks = doc.querySelectorAll('link[rel="stylesheet"]');
        const resolvedCssHrefs: string[] = [];
        const cssLoadPromises: Promise<void>[] = [];

        cssLinks.forEach((link) => {
          let href = link.getAttribute('href');
          if (!href) return;
          if (href.startsWith('/')) href = `${MARKETPLACE_APP_URL}${href}`;
          resolvedCssHrefs.push(href);

          const alreadyLoaded = Array.from(document.querySelectorAll('link[rel="stylesheet"]')).some(
            (existing) => existing.getAttribute('href') === href
          );
          if (alreadyLoaded) return;

          const newLink = document.createElement('link');
          newLink.rel = 'stylesheet';
          newLink.href = href;
          cssLoadPromises.push(
            new Promise<void>((resolve) => {
              newLink.onload = () => resolve();
              newLink.onerror = () => resolve();
            })
          );
          document.head.appendChild(newLink);
        });

        await Promise.all(cssLoadPromises);
        if (!isMountedRef.current || activeRequestIdRef.current !== requestId) return;

        const freshHtml = leftNav.outerHTML;
        writeCache({ html: freshHtml, cssHrefs: resolvedCssHrefs, selectedSchoolId, userId, organizationId });

        if (!hasCachedHtml) {
          // First load (no cache) — update UI with the fresh HTML.
          setState({
            navHtml: freshHtml,
            isActive: true,
            isLoading: false,
            cssHrefs: resolvedCssHrefs,
            selectedSchoolId,
            userId,
            organizationId,
          });
        }
        // When hasCachedHtml is true, we intentionally skip setState.
        // The user keeps seeing the cached version. The fresh HTML is in
        // sessionStorage and will be used on the next page load.
      } catch (error) {
        if (abortController.signal.aborted || activeRequestIdRef.current !== requestId) return;
        if (!hasCachedHtml && isMountedRef.current) {
          console.error('MirroredLeftNav: fetch failed.', error);
          setState({
            navHtml: null,
            isActive: false,
            isLoading: false,
            cssHrefs: [],
            selectedSchoolId,
            userId,
            organizationId,
          });
        }
      }
    };

    fetchNav();
    return () => {
      abortController.abort();
      if (activeRequestIdRef.current === requestId) {
        activeRequestIdRef.current += 1;
      }
    };
  }, [
    selectedSchoolId,
    userId,
    organizationId,
    isProgramContextPending,
    isProgramNavUnavailable,
    isMirroredLeftNavEnabled,
    token,
  ]);

  const stateMatchesCurrentScope =
    state.selectedSchoolId === selectedSchoolId && state.userId === userId && state.organizationId === organizationId;
  let visibleState: MirroredLeftNavState = state;

  if (!isMirroredLeftNavEnabled || isProgramNavUnavailable) {
    visibleState = { navHtml: null, isActive: false, isLoading: false, cssHrefs: [] };
  } else if (isProgramContextPending || !stateMatchesCurrentScope) {
    visibleState = { navHtml: null, isActive: false, isLoading: true, cssHrefs: [] };
  }

  return <MirroredLeftNavContext.Provider value={visibleState}>{children}</MirroredLeftNavContext.Provider>;
}

/**
 * Returns the shared mirrored left nav state. When `isActive` is true the
 * consumer should render the mirrored nav; when false, render the legacy nav.
 */
export function useMirroredLeftNav() {
  return useContext(MirroredLeftNavContext);
}

// ---------------------------------------------------------------------------
// Shared helpers used by MirroredLeftNav (the presentational component).
// Exported here to keep DOM-manipulation logic in one place.
// ---------------------------------------------------------------------------

/**
 * Semantic UI defines `.hidden { display: none !important }` which clobbers
 * Tailwind's responsive display overrides. This function replaces conflicting
 * Tailwind patterns (`hidden lg:block`, etc.) with inline styles.
 *
 * The injected nav is always rendered in its desktop state: desktop-only
 * wrappers (`hidden lg:*`) are force-shown, and the mobile-only counterparts
 * (`* lg:hidden` / `* md:hidden`, e.g. the compact network logo variant in
 * the nav branding) are force-hidden — our own CSS loads after Nova's, so
 * Tailwind's media-query utilities can't be relied on here either way.
 */
export function fixResponsiveDisplayClasses(root: HTMLElement) {
  const replacements: [string, string, string][] = [
    ['hidden', 'lg:block', 'block'],
    ['hidden', 'lg:flex', 'flex'],
    ['hidden', 'md:block', 'block'],
    ['hidden', 'md:flex', 'flex'],
    ['flex', 'lg:hidden', 'none'],
    ['block', 'lg:hidden', 'none'],
    ['flex', 'md:hidden', 'none'],
    ['block', 'md:hidden', 'none'],
  ];

  for (const [hiddenClass, responsiveClass, display] of replacements) {
    const escapedResponsive = CSS.escape(responsiveClass);
    root.querySelectorAll(`.${CSS.escape(hiddenClass)}.${escapedResponsive}`).forEach((el) => {
      el.classList.remove(hiddenClass, responsiveClass);
      (el as HTMLElement).style.display = display;
    });
  }
}

/**
 * Toggle an element's visibility using inline styles. We avoid toggling the
 * `hidden` class because Semantic UI's `.hidden { display: none !important }`
 * can't be overridden by Tailwind classes.
 */
export function toggleVisibility(el: HTMLElement) {
  const isHidden = el.classList.contains('hidden') || el.style.display === 'none';
  el.classList.remove('hidden');
  el.style.display = isHidden ? '' : 'none';
}

function showSpinner(link: HTMLAnchorElement): () => void {
  const trailingIcons = Array.from(link.querySelectorAll<HTMLElement | SVGElement>(':scope > .ml-auto'));
  trailingIcons.forEach((icon) => (icon.style.display = 'none'));
  const spinner = document.createElement('span');
  spinner.className = 'hero-arrow-path h-5 w-5 shrink-0 ml-auto animate-spin';
  spinner.setAttribute('aria-hidden', 'true');
  link.append(spinner);
  return () => {
    spinner.remove();
    trailingIcons.forEach((icon) => (icon.style.display = ''));
  };
}

async function switchSchool(link: HTMLAnchorElement): Promise<void> {
  const result = await switchContextAndVerify(link.dataset.membershipId!).catch(() => null);
  switch (result?.status) {
    case 'ok':
    case 'superseded':
      clearCache();
      window.location.reload();
      return;
    default:
      logError('MirroredLeftNav: school switch fell back to Nova', null, {
        membershipId: link.dataset.membershipId,
        status: result?.status ?? 'threw',
      });
      window.location.assign(link.href);
  }
}

/**
 * Sets up vanilla JS event delegation for dropdown toggles, mobile nav
 * open/close and the school switcher modal. All queries are scoped to
 * `container` to avoid cross-talk when the same HTML is injected in both
 * desktop and mobile layouts. Returns a function that removes the listeners.
 */
export function setupEventDelegation(container: HTMLElement) {
  const controller = new AbortController();
  const { signal } = controller;
  // Dropdown section toggles
  container.addEventListener(
    'click',
    (e) => {
      const button = (e.target as HTMLElement).closest('button[phx-click]');
      if (!button || !button.id) return;

      const id = button.id;
      const children = container.querySelector(`#${CSS.escape(`${id}-children`)}`);
      const chevronDown = container.querySelector(`#${CSS.escape(`${id}-chevron-down`)}`);
      const chevronUp = container.querySelector(`#${CSS.escape(`${id}-chevron-up`)}`);

      if (children) toggleVisibility(children as HTMLElement);
      if (chevronDown) toggleVisibility(chevronDown as HTMLElement);
      if (chevronUp) toggleVisibility(chevronUp as HTMLElement);
    },
    { signal }
  );

  // Mobile nav close button (the X icon uses phx-click)
  container.addEventListener(
    'click',
    (e) => {
      const closeLink = (e.target as HTMLElement).closest('#left-nav-mobile a[phx-click]');
      if (!closeLink) return;
      e.preventDefault();
      const mobileNav = container.querySelector('#left-nav-mobile') as HTMLElement | null;
      if (mobileNav) {
        mobileNav.style.display = 'none';
      }
    },
    { signal }
  );

  // School switcher modal. Nova's phx-* open/close handlers don't run here.
  const modalSelector = '[id$="-switch-school-modal"]';
  const isModalOpen = (modal: HTMLElement) => modal.style.display !== 'none' && !modal.classList.contains('hidden');
  const setModalOpen = (modal: HTMLElement, open: boolean) => {
    // Inline styles, not the `hidden` class: Semantic UI's `.hidden` is !important.
    modal.classList.remove('hidden');
    modal.style.display = open ? '' : 'none';
    modal.querySelector('[id$="-modal-container"]')?.classList.remove('hidden');
  };
  const setModalBusy = (modal: HTMLElement | null, busy: boolean) => {
    if (!modal) return;
    if (busy) modal.setAttribute('aria-busy', 'true');
    else modal.removeAttribute('aria-busy');
    modal.style.cursor = busy ? 'progress' : '';
    const content = modal.querySelector<HTMLElement>('[id$="-modal-content"]');
    if (content) content.style.opacity = busy ? '0.5' : '';
  };
  const openModals = () => Array.from(container.querySelectorAll<HTMLElement>(modalSelector)).filter(isModalOpen);

  container.addEventListener(
    'click',
    (e) => {
      const target = e.target as HTMLElement;
      const trigger = target.closest('[id^="mirror-nav-switch-school-trigger-"]');
      if (trigger) {
        e.preventDefault();
        const modal = container.querySelector<HTMLElement>(
          `#${CSS.escape(trigger.getAttribute('aria-controls') ?? '')}`
        );
        if (modal) setModalOpen(modal, true);
        return;
      }

      const membershipLink = target.closest<HTMLAnchorElement>('a[data-membership-id]');
      if (membershipLink) {
        e.preventDefault();
        const modal = membershipLink.closest<HTMLElement>(modalSelector);
        if (modal?.getAttribute('aria-busy') === 'true') return;
        setModalBusy(modal, true);
        const hideSpinner = showSpinner(membershipLink);
        void switchSchool(membershipLink).finally(() => {
          setModalBusy(modal, false);
          hideSpinner();
        });
        return;
      }

      for (const modal of openModals()) {
        if (!modal.contains(target)) continue;
        // The X button sits outside the content slot; claim/connect buttons inside it must not close.
        const closeButton = target.closest('button[phx-click]');
        const isX = closeButton && !closeButton.closest('[id$="-modal-content"]');
        const outsideCard = !target.closest('[id$="-modal-container"]');
        if (isX || outsideCard) setModalOpen(modal, false);
      }
    },
    { signal }
  );

  document.addEventListener(
    'keydown',
    (e) => {
      if (e.key === 'Escape') openModals().forEach((modal) => setModalOpen(modal, false));
    },
    { signal }
  );

  return () => controller.abort();
}
